import React, { useState } from 'react';
import {
  Paper, Grid, FormControl, InputLabel, Select, MenuItem, OutlinedInput,
  TextField, Slider, Box, Typography, Button, Stack, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, FormGroup, FormControlLabel, Checkbox,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  AnalyticsFilters, BreakdownDimension, FilterPreset, Granularity, ViewKey, REPORT_DESCRIPTION_MAX,
} from '../../services/analyticsService';

interface Props {
  value: AnalyticsFilters;
  options: Record<BreakdownDimension, string[]>;
  onChange: (next: AnalyticsFilters) => void;
  onExport: () => void;
  onReset: () => void;
  exporting?: boolean;
  presets: FilterPreset[];
  presetId: string | number | '';
  minDate?: string;   // lower bound for the da/to pickers (set only when it applies)
  isAdmin: boolean;
  onApplyPreset: (preset: FilterPreset) => void;
  onSavePreset: (
    name: string, view: ViewKey[], granularity?: Granularity, description?: string, lockDates?: boolean,
  ) => void;
  onEditDescription: (preset: FilterPreset, description: string) => void;
  // The active report fixes the dates for this viewer: the date fields are read-only.
  datesLocked?: boolean;
  onDeletePreset: (id: number) => void;
  granularity: Granularity;
  onReportExport: () => void;
  exportingReport?: boolean;
  canExportReport: boolean;
  // Chart texts edited on the page and not yet saved into a report.
  textsUnsaved?: boolean;
}

const DIMS: { key: BreakdownDimension; label: string }[] = [
  { key: 'intento', label: 'Motivo di contatto' },
  { key: 'macro_categoria', label: 'Ufficio/Categoria' },
  { key: 'servizio', label: 'Dettaglio/Servizio' },
];

// Kept in step with the BUILTIN_PRESETS names: this list is hardcoded separately
// from them, and letting the two drift leaves the admin with two vocabularies
// for the same sections.
const SECTIONS: { key: ViewKey; label: string }[] = [
  { key: 'kpi', label: 'Riepilogo chiamate' },
  { key: 'volume', label: 'Volume chiamate' },
  { key: 'motivo', label: 'Chiamate per motivo di contatto' },
  { key: 'categorie', label: 'Chiamate ufficio/categoria & dettaglio servizi' },
  { key: 'distribuzioni', label: 'Chiamate per periodo (giorno/durata/orario)' },
];

const GRANULARITY_LABELS: Record<Granularity, string> = {
  day: 'giorno', week: 'settimana', month: 'mese',
};

const FilterPanel: React.FC<Props> = ({
  value, options, onChange, onExport, onReset, exporting,
  presets, presetId, minDate, isAdmin, onApplyPreset, onSavePreset, onDeletePreset,
  granularity, onReportExport, exportingReport, canExportReport, textsUnsaved,
  onEditDescription, datesLocked,
}) => {
  const set = (patch: Partial<AnalyticsFilters>) => onChange({ ...value, ...patch });

  // Da/A are picked from the calendar only (CAI-19): typed dates bypassed the
  // bounds and "dal 22 al 13" showed zero instead of being refused. Keys other
  // than Tab are swallowed; Enter and Space open the calendar, so the field
  // stays usable from the keyboard.
  const calendarOnly = {
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Tab') return;
      e.preventDefault();
      if (e.key === 'Enter' || e.key === ' ') e.currentTarget.showPicker?.();
    },
    onPaste: (e: React.ClipboardEvent) => e.preventDefault(),
    onClick: (e: React.MouseEvent<HTMLInputElement>) => e.currentTarget.showPicker?.(),
  };
  // A ≥ Da, both above the minimum date. The calendar greys out the rest; the
  // check on change is the backstop for anything that still gets through.
  const toMin = [minDate, value.from].filter(Boolean).sort().pop();
  const setDate = (patch: { from?: string } | { to?: string }) => {
    const next = { ...value, ...patch };
    if (next.from && next.to && next.from > next.to) return;
    if (minDate && ((next.from && next.from < minDate) || (next.to && next.to < minDate))) return;
    onChange(next);
  };

  const satRange: number[] = [
    value.sat_min === '' || value.sat_min == null ? 0 : Number(value.sat_min),
    value.sat_max === '' || value.sat_max == null ? 10 : Number(value.sat_max),
  ];

  // Save-preset dialog (admin only)
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveView, setSaveView] = useState<ViewKey[]>(['kpi', 'volume', 'motivo', 'categorie', 'distribuzioni']);
  // Off by default: a preset that pins the granularity is the exception, and
  // leaving it unset is what "don't touch the current choice" means.
  const [saveGranularity, setSaveGranularity] = useState(false);
  const [saveDescription, setSaveDescription] = useState('');
  const [saveLockDates, setSaveLockDates] = useState(false);
  // Same rule as the page: a period, or both da and a.
  const hasDates = value.from || value.to ? !!(value.from && value.to) : !!value.period;

  const active = presets.find(p => String(p.id) === String(presetId));
  // Description editor for the active saved report (admin). A built-in has no
  // stored description to change.
  const [descOpen, setDescOpen] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const openDesc = () => { setDescDraft(active?.content.description ?? ''); setDescOpen(true); };
  const confirmDesc = () => {
    if (active) onEditDescription(active, descDraft.trim());
    setDescOpen(false);
  };

  // Saving upserts BY NAME. If the dialog opened blank while a saved report is
  // active, saving it again under its own name would wipe its description — so
  // the dialog starts from the active saved report. A built-in starts blank:
  // saving it creates a new report, it never overwrites the built-in.
  const openSave = () => {
    if (active && !active.builtin) {
      setSaveName(active.name);
      setSaveDescription(active.content.description ?? '');
      setSaveLockDates(!!active.content.lockDates);
    } else {
      setSaveName('');
      setSaveDescription('');
      setSaveLockDates(false);
    }
    setSaveOpen(true);
  };

  const toggleSection = (k: ViewKey) =>
    setSaveView(prev => (prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]));

  const confirmSave = () => {
    if (!saveName.trim() || saveView.length === 0) return;
    onSavePreset(saveName.trim(), saveView, saveGranularity ? granularity : undefined,
      saveDescription.trim(), saveLockDates);
    setSaveOpen(false);
    setSaveName('');
    setSaveDescription('');
  };

  const deletable = presets.filter(p => typeof p.id === 'number');

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2} alignItems="flex-end">
        {/* Report list + actions first: the report is chosen before the period. */}
        <Grid item xs={12}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {/* Controlled by the active preset so the selection is visible; Azzera
                resets it to '' (so the same preset can be re-applied afterwards). */}
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Lista report</InputLabel>
              <Select
                label="Lista report"
                value={presetId === '' ? '' : String(presetId)}
                // The closed field shows the name only; the description is for
                // choosing, in the open list.
                renderValue={v => presets.find(x => String(x.id) === String(v))?.name ?? ''}
                MenuProps={{ PaperProps: { sx: { maxWidth: 440 } } }}
                onChange={e => {
                  const p = presets.find(x => String(x.id) === String(e.target.value));
                  if (p) onApplyPreset(p);
                }}
              >
                {presets.length === 0 && <MenuItem value="" disabled>Nessun report</MenuItem>}
                {presets.map(p => (
                  <MenuItem key={String(p.id)} value={String(p.id)}
                    sx={{ flexDirection: 'column', alignItems: 'flex-start', whiteSpace: 'normal', py: 1 }}>
                    <Typography variant="body2">{p.name}{p.builtin ? '' : ' •'}</Typography>
                    {p.content.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45, mt: 0.25 }}>
                        {p.content.description}
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isAdmin && (
              <Button startIcon={<SaveIcon />} onClick={openSave}>Salva report</Button>
            )}
            {/* Edited chart texts live only on this page until a report is saved:
                changing report discards them. Said next to the one button that
                keeps them, so the cause and the remedy sit side by side. */}
            {isAdmin && textsUnsaved && (
              <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 500 }}>
                Testi dei grafici modificati, non ancora salvati
              </Typography>
            )}
            {isAdmin && deletable.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Elimina report</InputLabel>
                <Select
                  label="Elimina report"
                  value=""
                  onChange={e => {
                    const id = Number(e.target.value);
                    const p = deletable.find(x => x.id === id);
                    if (p && window.confirm(`Eliminare il report "${p.name}"?`)) onDeletePreset(id);
                  }}
                >
                  {deletable.map(p => (
                    <MenuItem key={String(p.id)} value={String(p.id)}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {isAdmin && <Button startIcon={<RestartAltIcon />} onClick={onReset}>Azzera</Button>}
            {/* Labels name what each file CONTAINS, not the format they share:
                two buttons both reading "Esporta XLSX" would hide the only
                difference that matters. */}
            <Button
              variant="contained" startIcon={<FileDownloadIcon />}
              onClick={onReportExport}
              disabled={exportingReport || !canExportReport}
              title={canExportReport ? undefined : 'Selezionare un report da scaricare'}
            >
              {exportingReport ? 'Scarico…' : 'Scarica report'}
            </Button>
            {isAdmin && (
              <Button variant="contained" startIcon={<FileDownloadIcon />} onClick={onExport} disabled={exporting}>
                {exporting ? 'Scarico…' : 'Scarica dati'}
              </Button>
            )}
          </Stack>
        </Grid>

        {/* The active report's description, where it can be read and changed —
            the list shows it only while open. */}
        {active && (active.content.description || (isAdmin && !active.builtin)) && (
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch', lineHeight: 1.55 }}>
              {active.content.description || 'Nessuna descrizione'}
            </Typography>
            {isAdmin && !active.builtin && (
              <IconButton size="small" aria-label="Modifica descrizione del report"
                title="Modifica descrizione del report" onClick={openDesc}
                sx={{ mt: '-4px', color: 'text.secondary' }}>
                <EditOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            )}
          </Grid>
        )}

        {/* Da/a only, for admins and users alike — no quick "Periodo" picker
            (CAI-25). Empty means no dates: no charts. Read-only when the active
            report fixes them. */}
        <Grid item xs={6} sm={3} md={2}>
          <TextField fullWidth size="small" type="date" label="Da" InputLabelProps={{ shrink: true }}
            inputProps={{ min: minDate, max: value.to || undefined, ...calendarOnly }} disabled={datesLocked}
            value={value.from ?? ''} onChange={e => setDate({ from: e.target.value })} />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <TextField fullWidth size="small" type="date" label="A" InputLabelProps={{ shrink: true }}
            inputProps={{ min: toMin, ...calendarOnly }} disabled={datesLocked}
            value={value.to ?? ''} onChange={e => setDate({ to: e.target.value })} />
        </Grid>
        {datesLocked && (
          <Grid item xs={12} sm="auto">
            <Typography variant="caption" color="text.secondary">Date fissate dal report</Typography>
          </Grid>
        )}

        {/* Taxonomy / toggle / range filters — ADMIN only (preset-authoring surface) */}
        {isAdmin && (
          <>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Trasferita</InputLabel>
                <Select label="Trasferita" value={value.trasferita ?? ''}
                  onChange={e => set({ trasferita: e.target.value as AnalyticsFilters['trasferita'] })}>
                  <MenuItem value="">Tutte</MenuItem>
                  <MenuItem value="1">Sì</MenuItem>
                  <MenuItem value="0">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Prenotazione</InputLabel>
                <Select label="Prenotazione" value={value.vuole_prenotare ?? ''}
                  onChange={e => set({ vuole_prenotare: e.target.value as AnalyticsFilters['vuole_prenotare'] })}>
                  <MenuItem value="">Tutte</MenuItem>
                  <MenuItem value="1">Sì</MenuItem>
                  <MenuItem value="0">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {DIMS.map(({ key, label }) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <FormControl fullWidth size="small">
                  <InputLabel>{label}</InputLabel>
                  <Select
                    multiple
                    value={value[key] ?? []}
                    onChange={e => set({ [key]: e.target.value as string[] } as Partial<AnalyticsFilters>)}
                    input={<OutlinedInput label={label} />}
                    renderValue={sel => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(sel as string[]).map(v => (
                          <Chip key={v} label={v} size="small"
                            onMouseDown={e => e.stopPropagation()}
                            onDelete={() => set({ [key]: (value[key] ?? []).filter(x => x !== v) } as Partial<AnalyticsFilters>)} />
                        ))}
                      </Box>
                    )}
                  >
                    {options[key].map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            ))}

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">Soddisfazione</Typography>
              <Box sx={{ px: 1 }}>
                <Slider value={satRange} min={0} max={10} step={1} marks valueLabelDisplay="auto"
                  onChange={(_e, v) => {
                    const [a, b] = v as number[];
                    set(a === 0 && b === 10 ? { sat_min: '', sat_max: '' } : { sat_min: a, sat_max: b });
                  }} />
              </Box>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth size="small" type="number" label="Durata min (s)"
                value={value.dur_min ?? ''} onChange={e => set({ dur_min: e.target.value === '' ? '' : Number(e.target.value) })} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth size="small" type="number" label="Durata max (s)"
                value={value.dur_max ?? ''} onChange={e => set({ dur_max: e.target.value === '' ? '' : Number(e.target.value) })} />
            </Grid>
          </>
        )}

      </Grid>

      <Dialog open={descOpen} onClose={() => setDescOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Descrizione del report</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth margin="dense" label="Descrizione del report" multiline minRows={3}
            value={descDraft}
            onChange={e => setDescDraft(e.target.value.slice(0, REPORT_DESCRIPTION_MAX))}
            helperText={`${descDraft.length}/${REPORT_DESCRIPTION_MAX} · cambia solo la descrizione, filtri e date restano quelli salvati`} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDescOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={confirmDesc}>Salva</Button>
        </DialogActions>
      </Dialog>

      {/* Save-preset dialog (admin) */}
      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Salva report</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth margin="dense" label="Nome"
            value={saveName} onChange={e => setSaveName(e.target.value)} />
          <TextField fullWidth margin="dense" label="Descrizione del report" multiline minRows={2}
            value={saveDescription}
            onChange={e => setSaveDescription(e.target.value.slice(0, REPORT_DESCRIPTION_MAX))}
            placeholder="A cosa serve questo report e per chi"
            helperText={`${saveDescription.length}/${REPORT_DESCRIPTION_MAX} · facoltativa, compare sotto il nome nella lista report`} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            Sezioni mostrate da questo report:
          </Typography>
          <FormGroup>
            {SECTIONS.map(s => (
              <FormControlLabel key={s.key}
                control={<Checkbox checked={saveView.includes(s.key)} onChange={() => toggleSection(s.key)} />}
                label={s.label} />
            ))}
          </FormGroup>
          <FormControlLabel
            control={<Checkbox checked={saveGranularity} onChange={() => setSaveGranularity(v => !v)} />}
            label={`Apri sempre il grafico volume per ${GRANULARITY_LABELS[granularity]}`}
          />
          <FormControlLabel
            control={<Checkbox checked={saveLockDates && hasDates} disabled={!hasDates}
              onChange={() => setSaveLockDates(v => !v)} />}
            label="Blocca le date: gli utenti non possono modificarle"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {hasDates
              ? 'I filtri correnti, date incluse, vengono salvati nel report.'
              : 'I filtri correnti vengono salvati nel report. Nessuna data impostata: il report si aprirà senza date.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={confirmSave} disabled={!saveName.trim() || saveView.length === 0}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FilterPanel;
